<template>
  <div v-if="Object.keys(booking_data).length" class="checkout">
    <!-- nel js c'è un router push a 'detail' se non trovo booking_data (es. se refresho la pagina checkout). questo ulteriore controllo è per evitare un undefined rischioso a template -->
    <b-container v-if="!(loading || purchasing)" class="checkout__content">
      <button-back :forwards_only="forwards_only" />
    </b-container>
    <b-container class="checkout__content">
      <section v-if="loading" class="section section--loader">
        <loader size="large" :show_timing_slot="true" />
      </section>
      <b-row>
        <b-col sm="12" md="7" lg="8" xl="9">
          <!-- form di checkout: visibile solo a caricamento finito e ordine non ancora concluso -->
          <section v-if="!loading && !checkout_ok" class="checkout__wrapper">
            <div class="checkout__elements checkout__elements--first">
              <reservation
                :contacts="booking_data.flightCreateOrderBody.data.contacts"
              />
              <flight-summary :flight_offer="flight_offer" />
            </div>
            <div class="checkout__elements checkout__elements--second">
              <travelers
                :travelers="booking_data.flightCreateOrderBody.data.travelers"
              />
              <automated-check-in
                v-if="automated_check_in_available"
                :selected="automated_check_in_requested"
                :price="automated_check_in_price"
                @select="(value) => (automated_check_in_requested = value)"
              />
              <flight-bags :flight_offer="flight_offer" />
              <additional-services-booked
                :flight_offer="flight_offer"
                :travelers="traveler_details"
              />
            </div>
          </section>

          <!-- pannello di esito a checkout concluso -->
          <div
            v-else-if="!loading && checkout_ok"
            class="checkout__panel"
            compact
          >
            <p v-if="checkout_msg">
              <b-alert variant="success" show v-html="checkout_msg" />
            </p>
          </div>
        </b-col>
        <b-col m="12" md="5" lg="4" xl="3">
          <sidebar
            :integration="integration"
            :availability="sidebar_availability"
            :live_price="live_price"
            :loading="loading"
            :purchasing="purchasing"
            :completed="checkout_ok"
            :discount_codes="discount_codes"
            @purchase="
              (sidebar_data) => {
                purchase(sidebar_data);
              }
            "
            @commit="
              (event) => {
                this.$store.commit(event.name, event.payload);
              }
            "
          >
            <template #resume>
              <sidebar-resume
                :flight_offers="[flight_offer]"
                :uncommitted_additions="uncommitted_additions"
                contained
              />
            </template>

            <template #message>
              <div v-if="checkout_ok" class="vb-sidebar__block">
                <div v-if="checkout_msg" v-html="checkout_msg"></div>
              </div>
              <div v-if="!checkout_ok && purchasing" class="text-center">
                <loader />
              </div>
            </template>
          </sidebar>
        </b-col>
      </b-row>

      <loader-modal :show="!checkout_ok && purchasing" service="book" />
    </b-container>
  </div>
</template>

<script>
  import Vue from 'vue';
  import { mapState } from 'vuex';
  import {
    book,
    retrieve,
    get_offer_details,
    has_capability_for,
    get_automated_checkin_offer,
  } from '@liveflight/providers';
  import { parse_flight_offer_for_ecommerce_item } from '@liveflight/utilities/mixins';
  import { get_markup } from '@liveflight/utilities/price';
  import { number, sum } from 'mathjs';
  import {
    reverse_date,
    custom_event,
    get_availability,
    log,
  } from '@/utilities/commons';
  import FlightBags from '@liveflight/components/blocks/flight-bags.vue';
  import SidebarResume from '@liveflight/components/blocks/resume/resume';
  import Reservation from '@liveflight/components/blocks/reservation.vue';
  import Travelers from '@liveflight/components/blocks/travelers.vue';
  import FlightSummary from '@liveflight/components/blocks/flight-summary.vue';
  import Sidebar from '@components/blocks/sidebar/sidebar';
  import AdditionalServicesBooked from '@liveflight/components/blocks/additional-services-booked.vue';
  import AutomatedCheckIn from '@liveflight/components/blocks/check-in/automated-check-in.vue';

  export default {
    name: 'checkout',

    components: {
      'additional-services-booked': AdditionalServicesBooked,
      'automated-check-in': AutomatedCheckIn,
      'flight-bags': FlightBags,
      reservation: Reservation,
      travelers: Travelers,
      'flight-summary': FlightSummary,
      'sidebar-resume': SidebarResume,
      sidebar: Sidebar,
    },

    data() {
      return {
        vm: this,
        loading: true,
        purchasing: false,
        integration: Vue.prototype.$config.integration,
        checkout_ok: false,
        checkout_msg: '',
        automated_check_in_available: false,
        automated_check_in_requested: false,
        // null until the probe quotes a fee. Absent price = free service, which
        // is every response the backend sends today.
        automated_check_in_price: null,
      };
    },

    computed: {
      ...mapState(['discount_codes', 'booking_data']),
      ...mapState('liveflight', [
        'flight_offer',
        'traveler_details',
        'contact_details',
        'guests',
        'held_booking',
      ]),
      flight_live_price() {
        return this.booking_data.live_price || '0';
      },
      /**
       * The check-in fee as an uncommitted addition, or [] when the service is
       * free or not opted into. `is_final_price` keeps the sidebar from
       * applying the flight markup: the backend already quoted this amount to
       * the customer.
       */
      uncommitted_additions() {
        if (!this.automated_check_in_requested) {
          return [];
        }
        if (!Number(this.automated_check_in_fee)) {
          return [];
        }
        return [
          {
            type: 'AUTOMATED_CHECK_IN',
            amount: this.automated_check_in_fee,
            price: this.automated_check_in_price,
            is_final_price: true,
            info: {
              automated_check_in: {
                description: 'Check-in automatico',
                icon: 'flight_ticket',
              },
            },
          },
        ];
      },
      automated_check_in_fee() {
        const { grandTotal = '', total = '' } =
          this.automated_check_in_price || {};
        const amount = grandTotal === '' ? total : grandTotal;
        return Number(amount) || 0;
      },
      /**
       * What the customer pays and what `book` reports as `live_price`: the
       * marked-up fare plus any uncommitted addition, which carries no markup
       * of its own. Drives the sidebar total and the welfare credit check too,
       * so all three agree on one number.
       *
       * With nothing uncommitted the fare value is returned untouched, never
       * re-derived: `number()` would normalize `'1234.00'` to `'1234'` and
       * quietly change what gets posted to `book` on the ordinary free path.
       */
      live_price() {
        if (!this.uncommitted_additions.length) {
          return this.flight_live_price;
        }
        return String(
          sum(
            number(this.flight_live_price),
            number(this.automated_check_in_fee)
          )
        );
      },
      forwards_only() {
        return has_capability_for(this.flight_offer, 'forwardsOnly');
      },
      sidebar_availability() {
        return get_availability(this.live_price);
      },
    },

    created() {
      this.reverse_date = reverse_date;
      this.custom_event = custom_event;
      // se non trovo booking data rimando al dettaglio
      if (!Object.keys(this.booking_data).length) {
        this.$router.replace('detail');
        return;
      }

      // per evitare undefined quando li popolo dinamicamente
      if (this.booking_data.payments === undefined) {
        this.booking_data.payments = {
          cc: {},
          welfare: {},
          discountCodes: {},
        };
      }
    },

    mounted() {
      custom_event('initiateCheckout', this.flight_offer.price.grandTotal);

      const item = parse_flight_offer_for_ecommerce_item(this.flight_offer);

      custom_event({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'EUR',
          items: [item],
          transaction_id: '',
          affiliation: '',
          value: item.price,
          tax: 0,
          shipping: 0,
          coupon: '',
        },
      });
      custom_event({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'EUR',
          items: [item],
          transaction_id: '',
          affiliation: '',
          value: item.price,
          tax: 0,
          shipping: 0,
          coupon: '',
        },
      });

      this.refresh_offer()
        .then((flight_offer) => {
          if (!flight_offer) {
            log('checkout.refresh missing offer');
            window.eventBus.$emit('got_toast_notification', {
              text: 'Non siamo riusciti ad aggiornare il riepilogo: i totali mostrati potrebbero non essere aggiornati.',
              variant: 'warning',
            });
            return;
          }
          this.$store.commit('liveflight/flight_offer', flight_offer);
          this.$store.commit('booking_data', {
            ...this.booking_data,
            live_price: get_markup(flight_offer).mu_price,
          });

          if (has_capability_for(flight_offer, 'automatedCheckIn')) {
            this.get_automated_checkin(flight_offer);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    methods: {
      get_automated_checkin(flight_offer) {
        const { country_code } = this.contact_details || {};
        get_automated_checkin_offer(flight_offer, country_code).then(
          (offer) => {
            const { available = false, price = null } = offer || {};
            this.automated_check_in_available = available;
            this.automated_check_in_price = price;
          }
        );
      },
      refresh_offer() {
        // ancillariesAsStep providers: re-pull the held booking so totals
        // reflect what book_ancillaries committed before unblocking the page.
        const should_retrieve =
          has_capability_for(this.flight_offer, 'ancillariesAsStep') &&
          this.held_booking &&
          this.held_booking.id;

        if (should_retrieve) {
          return retrieve(this.flight_offer, this.held_booking.id).then(
            ({ flight_offer }) => flight_offer
          );
        }
        return get_offer_details(this.flight_offer);
      },
      purchase(sidebar_data) {
        // I pagamenti sono già preparati dalla sidebar generica
        this.booking_data.payments = sidebar_data.payments;

        // Prepara i dati guest dal payload della sidebar
        const guestPayload =
          (sidebar_data.guest && sidebar_data.guest.payload) || {};
        const guests = {
          guestFirstName: guestPayload.guestFirstName,
          guestLastName: guestPayload.guestLastName,
          guestFiscalCode: guestPayload.guestFiscalCode,
          guestEmail: guestPayload.guestEmail,
          guestEmailConf: guestPayload.guestEmailConf,
          guestPrivacy: guestPayload.guestPrivacy,
          beneficiaryId: guestPayload.beneficiaryId || 0,
        };
        if (guestPayload.guestPhone) {
          guests.guestPhone = guestPayload.guestPhone;
        }
        this.$store.commit('liveflight/guests', guests);

        this.loading = true;
        this.purchasing = true;
        this.scroll_top();

        book(this.flight_offer, {
          travelers: this.traveler_details,
          contact: this.contact_details,
          payments: this.booking_data.payments,
          guest: this.guests,
          heldBooking: this.held_booking,
          livePrice: this.live_price,
          automatedCheckInRequested: this.automated_check_in_requested,
        }).then(
          (success) => {
            this.scroll_top();
            const { status = 500, data = {} } = success || {};
            this.checkout_msg = data.msg;
            this.loading = false;
            this.purchasing = false;

            // 202 = asyncPayment in flight; not a finalized success.
            if (status === 202) {
              this.checkout_ok = true;
              return;
            }

            // Envelope-level error: BE returned 200 but with `data.errors`.
            if (data.errors) {
              custom_event('checkoutError');
              return;
            }

            this.checkout_ok = true;
            const {
              confirmcode: confirmCode,
              updateAvailabilityTo,
              flightVoucherId,
              kyteVoucherId,
            } = data;
            custom_event(
              'confirmedCheckout',
              this.flight_offer.price.grandTotal
            );

            const item = parse_flight_offer_for_ecommerce_item(
              this.flight_offer
            );
            custom_event({
              event: 'purchase',
              ecommerce: {
                currency: 'EUR',
                items: [item],
                transaction_id: confirmCode,
                affiliation: '',
                value: item.price,
                tax: 0,
                shipping: 0,
                coupon: '',
              },
            });
            if (updateAvailabilityTo) {
              Vue.prototype.$config.integration.availability =
                updateAvailabilityTo;
            }

            const resolved = this.$router.resolve({
              name: 'confirm',
              params: {
                flight_voucher_id: flightVoucherId || kyteVoucherId,
                confirm_code: confirmCode,
              },
            });
            window.location.href = resolved.href;
          },
          (error) => {
            log('checkout.purchase', error);
            this.loading = false;
            this.purchasing = false;
            custom_event('checkoutError');
          }
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .checkout {
    margin-bottom: $grid-gutter-width * 4;

    &__panel {
      @extend .content-panel !optional;
      margin-bottom: 1rem !important;
    }

    .privacy-box {
      display: flex;
    }

    &__elements {
      &--first {
        margin-bottom: $grid-gutter-20;

        @include media-breakpoint-up(xl) {
          display: flex;
          align-self: stretch;

          ::v-deep {
            .content-section {
              height: 100%;
              margin-bottom: 0;
            }
          }

          .reservation {
            flex-basis: 50%;
            margin-right: $grid-gutter-20 / 2;
          }
          .flight-summary {
            flex-basis: 50%;
            margin-left: $grid-gutter-20 / 2;
          }
        }
      }
    }

    .btn-light {
      .chevron svg {
        width: 10px;
        height: auto;

        path {
          stroke: $cta;
        }
      }
    }

    &__content {
      padding-top: $grid-gutter-width;
    }

    /*::v-deep {
      .reservation, .flight-summary {
        height: 100%;

        .content-section {
          height: 100%;
        }
      }
    }*/
  }
</style>
